import type { ReactElement } from 'react';
import { Navigate, useLocation, useParams, useRoutes } from 'react-router-dom';
import { complianceRoutes } from '@/capabilities/compliance-assessment/routes';
import { EMAIL_SIGNATURE_ROUTE_DEFINITIONS } from '@/capabilities/email-signatures/routes';
import { DATA_TRANSFER_ROUTE_DEFINITIONS } from '@/capabilities/data-transfer/routes';
import { REMOTE_ACCESS_ROUTE_DEFINITIONS } from '@/capabilities/remote-access/routes';
import { COMMUNICATIONS_ROUTE_DEFINITIONS } from '@/capabilities/communications/routes';
import { MICROSOFT_365_MANAGEMENT_ROUTE_DEFINITIONS } from '@/capabilities/microsoft-365-management/routes';
import { PROJECT_DELIVERY_ROUTE_DEFINITIONS } from '@/capabilities/project-delivery/routes';
import { SERVICE_OPERATIONS_ROUTE_DEFINITIONS } from '@/capabilities/service-operations/routes';
import { WORKFLOW_AUTOMATION_ROUTE_DEFINITIONS } from '@/workflows/automation/routes';
import type { DestinationId } from '@/app/config/destinations';
import {
  deviceOperationsHref,
  mftOperationsHref,
  mjolnirOperationsHref,
  networkOperationsHref,
  remoteOperationsHref,
  scriptOperationsHref,
  type MftSegment,
  type MjolnirOperationsView,
  type NetworkOperationsView,
  type ScriptOperationsSegment,
} from '@/app/navigation/routes/workbenchPaths';
import { DISPOSITION_BY_PATH } from '@/app/config/route-disposition';
import { Route } from 'react-router-dom';
import { DestinationCapabilityFrame } from './DestinationCapabilityFrame';
import { TenantAdministrationAccessGuard } from '@/platform/tenant-administration/pages/tenant-administration/TenantAdministrationAccessGuard';
import type { DestinationCapabilityDefinition } from './destinationCapabilityTypes';
import {
  GlobalSearch,
  InsightsPage,
  KBEditor,
  NotificationCenter,
  OperationsDashboardPage,
  ReportHistory,
  ReportSchedules,
  ScanScheduleTemplates,
  TenantComparisonPage,
} from '@/app/administration/routes/lazyPages';
import {
  PatchCompliancePage,
  PrometheusDashboard,
  ReleasesPage,
  RolloutPage,
} from '@/capabilities/agent-release-management/public/routes/lazyPages';
import {
  AssetDashboard,
  AssetDisposal,
  AssetRegistry,
  CertificateList,
  DomainTracker,
  LeaseTracker,
  LicenseInventory,
  RefreshCycle,
  RenewalCalendar,
  ServiceDesk,
  SiteDocumentation,
  UptimeMonitor,
  WarrantyTracker,
} from '@/capabilities/asset-lifecycle/public/routes/lazyPages';
import {
  PersonaActionQueue,
  PersonaActivityLog,
  PersonaEditor,
  PersonaList,
} from '@/capabilities/automation-personas/public/routes/lazyPages';
import {
  BackupAlerts,
  BackupAssetScopes,
  BackupCompliance,
  BackupDashboard,
  BackupJobs,
  BackupProviderWizard,
  BackupProviders,
  BackupReports,
  RestoreTestHistory,
  RestoreTestWizard,
} from '@/capabilities/backup-management/public/routes/lazyPages';
import { BillingPage } from '@/capabilities/billing/public/routes/lazyPages';
import {
  CredentialVault,
  CredentialHealthScanDetail,
} from '@/capabilities/cerberus/public/routes/lazyPages';
import {
  CloudGovernanceDashboard,
  RateLimitsPage,
  RecordsroomOperationsPage,
} from '@/capabilities/cloud-governance/public/routes/lazyPages';
import {
  DataExportPage,
  CommunicationsMigrationDashboard,
  MigrationAssessment,
  MigrationHistoryPage,
  VoicemailDashboard,
} from '@/capabilities/communications-migration/public/routes/lazyPages';
import {
  AuditEditor,
  DeadlineReadiness,
} from '@/capabilities/compliance-assessment/public/routes/lazyPages';
import {
  BaselineManagement,
  MonitoringPolicies,
} from '@/capabilities/configuration-drift/public/routes/lazyPages';
import {
  AssessorRules,
  BenchmarkCatalogue,
  BillingDashboard,
  ContactDetail,
  ContactList,
  ContentBlockLibrary,
  ContractList,
  Day1PlanPage,
  EngagementDashboard,
  EstimateList,
  MSABuilder,
  MSAComparator,
  NewClientOnboardingPage,
  PendingChangesPage,
  PresentationMaker,
  PriceComparison,
  PricingCalculator,
  ProductGapAnalysis,
  ProfitabilityDashboard,
  ProposalComposer,
  ProposalList,
  ProposalPipeline,
  ProposalWizardPage,
  QBRBuilderPage,
  QBRPage,
  RenewalCalendarPage,
  RiskRegister,
  RoadmapBuilderPage,
  RoadmapEditor,
  RoadmapList,
  SatisfactionDashboard,
  ServiceCatalog,
  ServiceTerms,
  TechRoadmapEditor,
  TechRoadmapList,
  TechRoadmapTemplates,
} from '@/capabilities/customer-lifecycle/public/routes/lazyPages';
import {
  AgentConfigDetail,
  AgentConfigPolicies,
  AgentDeploy,
  AgentList,
  AgentLogViewer,
  AgentPushWizard,
  AgentUpdates,
  AlertCatalog,
  AlertDedupRules,
  AppDeployments,
  AppStore,
  AutoProvisionPage,
  BroadcastCommand,
  CloudAllyDashboard,
  CloudflareGatewayDashboard,
  SecuritySuitePage,
  CodeTwoDashboard,
  ConfigBaselines,
  ConfigDrift,
  CwRmmImport,
  DNSFilterDashboard,
  DashlaneDashboard,
  DeviceAssessmentReports,
  DeviceDetail,
  DeviceGroupRules,
  DeviceGroups,
  DeviceHealthHistory,
  DeviceOperationsShell,
  EndpointRuntimeDashboard,
  EventLogViewer,
  FleetHealthDashboard,
  FleetOverview,
  GPOManager,
  HuntressDashboard,
  HygieneDashboard,
  HygienePolicyDetail,
  IncidentCockpit,
  InstallLinks,
  IronscalesDashboard,
  PlanningQueue,
  PlaybookDetail,
  PlaybookLibrary,
  PolicyManagement,
  PrintixDashboard,
  QuarterlyReportPage,
  RegistryEditor,
  SecurityIncidents,
  SiteAssessmentScanResults,
  SoftwareRemovalPlanning,
  VendorDashboard,
  VendorDeploy,
  VendorDeploymentHistory,
  VendorOnboarding,
  VendorProfiles,
  VendorStatus,
  VulnerabilityDashboard,
} from '@/capabilities/device-management/public/routes/lazyPages';
import {
  AdminGuardPage,
  LicenseManagementPage,
  MfaEnrollmentPage,
  UsagePage,
} from '@/capabilities/tenant-governance/public/routes/lazyPages';
import {
  InactiveUsers,
  IntuneAppApprovalsPage,
  IntuneEnrollmentPosturePage,
  IntuneRemediationsPage,
  PowerAutomateInventoryPage,
  RiskyAgents,
  SignInLogs,
  UsageAnalytics,
} from '@/capabilities/microsoft-365-management/public/routes/lazyPages';
import {
  HardwareProfiles,
  MicrosoftLicenses,
  ProcurementOverview,
  OrderTrackingPage,
  ProcurementBundles,
  ProcurementPage,
  ProductSearch,
  PurchaseOrderDetailPage,
  PurchaseOrdersPage,
} from '@/capabilities/procurement/public/routes/lazyPages';
import {
  AutomationOpportunity,
  ClientBenchmark,
  ClientDeepDive,
  ClientHealthBoard,
  ComplianceMatrix,
  CostOfInactionView,
  DeliveryPipeline,
  EngineerWorkload,
  NarrativeBuilderPage,
  NarrativeKindPicker,
  NarrativeList,
  NarrativeStartPaths,
  ResolutionFeedInsight,
  RevenueProfitability,
} from '@/capabilities/service-intelligence/public/routes/lazyPages';
import {
  BillingCancel,
  BillingSuccess,
  TenantAdministrationDashboard,
  CurrentLicensePage,
  TenantList,
} from '@/platform/tenant-administration/routes/lazyPages';
import { TrustStreamPage } from '@/platform/trust/routes/lazyPages';
import {
  IdentityLifecycleDashboard,
  OffboardingWizard,
  OnboardingWizard,
  WorkflowProgress,
} from '@/workflows/customer-onboarding/public/routes/lazyPages';
import { MSPSetupHub, PsaImportPage } from '@/workflows/tenant-setup/public/routes/lazyPages';
import {
  DeviceMigrationDashboard,
  DeviceMigrationDetail,
  DeviceMigrationWizard,
  GWorkspaceMigrationWizard,
  ProfileMigrationPage,
} from '@/workflows/migration/public/routes/lazyPages';

export type { DestinationCapabilityDefinition } from './destinationCapabilityTypes';

function ComplianceRoutes() {
  return useRoutes(complianceRoutes);
}

function capability(
  capabilityPath: string,
  label: string,
  element: ReactElement
): DestinationCapabilityDefinition {
  const disposition = DISPOSITION_BY_PATH[capabilityPath];
  if (disposition?.disposition !== 'capability') {
    throw new Error(`Capability ${capabilityPath} requires a capability disposition.`);
  }
  return {
    capabilityPath,
    routePath: capabilityPath.replace(/^\//, ''),
    destination: disposition.destination,
    label,
    element,
  };
}

function MftLegacyRedirect({
  segment,
  drawerMode,
}: {
  segment: MftSegment;
  drawerMode?: 'new' | 'detail' | 'edit';
}) {
  const location = useLocation();
  const { jobId } = useParams<{ jobId: string }>();
  const drawer =
    drawerMode === 'new'
      ? 'mft-job-new'
      : drawerMode === 'detail' && jobId
        ? `mft-job:${jobId}`
        : drawerMode === 'edit' && jobId
          ? `mft-job-edit:${jobId}`
          : null;
  return <Navigate to={mftOperationsHref(segment, location.search, drawer)} replace />;
}

function ScriptLegacyRedirect({
  segment,
  detail,
}: {
  segment: ScriptOperationsSegment;
  detail?: boolean;
}) {
  const location = useLocation();
  const { scriptId } = useParams<{ scriptId: string }>();
  const sourceParams = new URLSearchParams(location.search);
  const drawer = detail && scriptId ? `script:${scriptId}` : null;
  const target = new URL(
    scriptOperationsHref(segment, location.search, drawer),
    'https://hyperion.local'
  );
  if (detail && sourceParams.get('run') === '1') target.searchParams.set('run', '1');
  return <Navigate to={`${target.pathname}${target.search}`} replace />;
}

function RemoteLegacyRedirect({ detail = false }: { detail?: boolean }) {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  return <Navigate to={remoteOperationsHref(location.search, detail ? id : null)} replace />;
}

function PatchLegacyRedirect({ detail = false }: { detail?: boolean }) {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const target = new URL(
    deviceOperationsHref('patches', location.search),
    'https://hyperion.local'
  );
  if (detail && id) target.searchParams.set('rollout', id);
  return <Navigate to={`${target.pathname}${target.search}`} replace />;
}

function MjolnirLegacyRedirect({ view }: { view: MjolnirOperationsView }) {
  const location = useLocation();
  return <Navigate to={mjolnirOperationsHref(view, location.search)} replace />;
}

function NetworkLegacyRedirect({ view }: { view: NetworkOperationsView }) {
  const location = useLocation();
  return <Navigate to={networkOperationsHref(view, location.search)} replace />;
}

/**
 * Existing implementation components hosted contextually by one of the five
 * product destinations. These are capabilities, not standalone product
 * surfaces. Only their destination-owned paths are mounted.
 */
export const DESTINATION_CAPABILITIES: readonly DestinationCapabilityDefinition[] = [
  capability('/devices', 'Devices', <DeviceOperationsShell />),
  capability('/devices/:id', 'Device Detail', <DeviceDetail />),
  capability('/devices/:id/health', 'Device Health History', <DeviceHealthHistory />),
  ...DATA_TRANSFER_ROUTE_DEFINITIONS.map(({ capabilityPath, label, element }) =>
    capability(capabilityPath, label, element)
  ),
  capability('/remote', 'Remote Control', <RemoteLegacyRedirect />),
  capability('/remote/:id', 'Remote Control', <RemoteLegacyRedirect detail />),
  capability('/patches', 'Patch Management', <PatchLegacyRedirect />),
  capability('/patches/rollouts', 'Patch Rollout', <PatchLegacyRedirect />),
  capability('/patches/rollouts/:id', 'Patch Rollout', <PatchLegacyRedirect detail />),
  capability('/agents', 'Agent List', <AgentList />),
  capability('/agents/deploy', 'Agent Deploy', <AgentDeploy />),
  capability('/agents/deploy/push', 'Agent Push Wizard', <AgentPushWizard />),
  capability('/devices/install-links', 'Install Links', <InstallLinks />),
  capability('/agents/updates', 'Agent Updates', <AgentUpdates />),
  capability('/agents/:id/logs', 'Agent Log Viewer', <AgentLogViewer />),
  capability('/devices/fleet-overview', 'Fleet Overview', <FleetOverview />),
  capability('/device-groups', 'Device Groups', <DeviceGroups />),
  capability('/devices/broadcast', 'Broadcast Command', <BroadcastCommand />),
  capability('/network/topology', 'Network Topology', <NetworkLegacyRedirect view="topology" />),
  capability(
    '/network/configs',
    'Network Device Configs',
    <NetworkLegacyRedirect view="configs" />
  ),
  capability(
    '/device-management/network-monitoring/alerts',
    'Network Monitoring Alerts',
    <NetworkLegacyRedirect view="alerts" />
  ),
  capability(
    '/device-management/ups',
    'Infrastructure Monitoring',
    <NetworkLegacyRedirect view="infrastructure" />
  ),
  capability(
    '/device-management/device-assessment',
    'Device Assessment Reports',
    <DeviceAssessmentReports />
  ),
  capability(
    '/device-management/site-assessment',
    'Site Assessment Scan Results',
    <SiteAssessmentScanResults />
  ),
  capability('/device-management/mft', 'MFT Dashboard', <MftLegacyRedirect segment="overview" />),
  capability('/device-management/mft/jobs', 'MFT Job List', <MftLegacyRedirect segment="jobs" />),
  capability(
    '/device-management/mft/jobs/new',
    'MFT Job Wizard',
    <MftLegacyRedirect segment="jobs" drawerMode="new" />
  ),
  capability(
    '/device-management/mft/jobs/:jobId',
    'MFT Job Detail',
    <MftLegacyRedirect segment="jobs" drawerMode="detail" />
  ),
  capability(
    '/device-management/mft/jobs/:jobId/edit',
    'MFT Job Wizard',
    <MftLegacyRedirect segment="jobs" drawerMode="edit" />
  ),
  capability(
    '/device-management/mft/endpoints',
    'MFT Endpoints',
    <MftLegacyRedirect segment="endpoints" />
  ),
  capability('/mjolnir', 'Mjolnir Profiles', <MjolnirLegacyRedirect view="profiles" />),
  capability('/mjolnir/sessions', 'Mjolnir Sessions', <MjolnirLegacyRedirect view="sessions" />),
  capability('/mjolnir/playbooks', 'Mjolnir Playbooks', <MjolnirLegacyRedirect view="playbooks" />),
  capability('/vendor/profiles', 'Vendor Profiles', <VendorProfiles />),
  capability('/vendor/deploy', 'Vendor Deploy', <VendorDeploy />),
  capability('/vendor/status', 'Vendor Status', <VendorStatus />),
  capability('/vendor/huntress', 'Huntress Dashboard', <HuntressDashboard />),
  capability('/vendor/cloudflare', 'Cloudflare Gateway', <CloudflareGatewayDashboard />),
  capability('/vendor/security-suite', 'Security Suite', <SecuritySuitePage />),
  capability('/vendor/dnsfilter', 'DNS Filter Dashboard', <DNSFilterDashboard />),
  capability('/vendor/dashlane', 'Dashlane Dashboard', <DashlaneDashboard />),
  capability('/vendor/ironscales', 'Ironscales Dashboard', <IronscalesDashboard />),
  capability('/vendor/cloudally', 'Cloud Ally Dashboard', <CloudAllyDashboard />),
  capability('/vendor/codetwo', 'Code Two Dashboard', <CodeTwoDashboard />),
  capability('/vendor/printix', 'Printix Dashboard', <PrintixDashboard />),
  capability('/vendor/dashboard', 'Vendor Dashboard', <VendorDashboard />),
  capability('/vendor/deployments', 'Vendor Deployment History', <VendorDeploymentHistory />),
  capability('/vendor/onboarding', 'Vendor Onboarding', <VendorOnboarding />),
  capability('/device-management/app-store', 'App Store', <AppStore />),
  capability('/device-management/app-store/deployments', 'App Deployments', <AppDeployments />),
  capability(
    '/device-management/app-store/removal',
    'Software Removal Planning',
    <SoftwareRemovalPlanning />
  ),
  capability(
    '/device-management/config-policies/:id',
    'Agent Config Detail',
    <AgentConfigDetail />
  ),
  capability('/device-management/config/baselines', 'Config Baselines', <ConfigBaselines />),
  capability('/device-management/config/drift', 'Config Drift', <ConfigDrift />),
  capability('/device-management/config/gpo', 'GPO Manager', <GPOManager />),
  capability('/device-management/devices/:id/event-log', 'Event Log Viewer', <EventLogViewer />),
  capability(
    '/network/docs',
    'Network Documentation',
    <NetworkLegacyRedirect view="documentation" />
  ),
  capability(
    '/network/credentials',
    'Network Credentials',
    <NetworkLegacyRedirect view="credentials" />
  ),
  capability(
    '/device-management/network-monitoring',
    'Network Monitoring Intelligence',
    <NetworkLegacyRedirect view="intelligence" />
  ),
  capability('/device-management/devices/:id/registry', 'Registry Editor', <RegistryEditor />),
  capability('/device-management/hygiene', 'Hygiene Dashboard', <HygieneDashboard />),
  capability(
    '/device-management/hygiene/:policyId',
    'Hygiene Policy Detail',
    <HygienePolicyDetail />
  ),
  capability(
    '/scripts/:scriptId',
    'Script Detail',
    <ScriptLegacyRedirect segment="library" detail />
  ),
  capability('/playbooks', 'Playbook Library', <PlaybookLibrary />),
  capability('/playbooks/:id', 'Playbook Detail', <PlaybookDetail />),
  capability('/cwrmm-import', 'Cw Rmm Import', <CwRmmImport />),
  ...EMAIL_SIGNATURE_ROUTE_DEFINITIONS.map(({ capabilityPath, label, element }) =>
    capability(capabilityPath, label, element)
  ),
  capability('/tenant-governance', 'Current Licence', <CurrentLicensePage />),
  capability('/tenant-governance/licenses', 'License Management', <LicenseManagementPage />),
  capability('/tenant-governance/usage', 'Usage', <UsagePage />),
  capability('/tenant-governance/mfa', 'MFA Enrollment', <MfaEnrollmentPage />),
  capability('/tenant-governance/admin-guard', 'Admin Guard', <AdminGuardPage />),
  capability('/prometheus', 'Prometheus Dashboard', <PrometheusDashboard />),
  capability('/prometheus/releases', 'Releases', <ReleasesPage />),
  capability('/prometheus/rollout', 'Rollout', <RolloutPage />),
  capability('/prometheus/compliance', 'Patch Compliance', <PatchCompliancePage />),
  capability('/onboarding/new-client', 'New Client Onboarding', <NewClientOnboardingPage />),
  capability('/proposals', 'Proposal List', <ProposalList />),
  capability('/contracts', 'Contract List', <ContractList />),
  capability('/contacts', 'Contact List', <ContactList />),
  capability('/contacts/:id', 'Contact Detail', <ContactDetail />),
  capability('/proposals/pipeline', 'Proposal Pipeline', <ProposalPipeline />),
  capability('/proposals/library', 'Content Block Library', <ContentBlockLibrary />),
  capability('/service-catalog', 'Service Catalog', <ServiceCatalog />),
  capability('/pricing', 'Pricing Calculator', <PricingCalculator />),
  capability('/profitability', 'Profitability Dashboard', <ProfitabilityDashboard />),
  capability('/customer-lifecycle/gaps', 'Product Gap Analysis', <ProductGapAnalysis />),
  capability('/customer-lifecycle/prices', 'Price Comparison', <PriceComparison />),
  capability('/customer-lifecycle/engagement', 'Engagement Dashboard', <EngagementDashboard />),
  capability('/customer-lifecycle/renewals', 'Renewal Calendar', <RenewalCalendarPage />),
  capability('/qbr', 'QBR', <QBRPage />),
  capability('/qbr/new', 'QBR Builder', <QBRBuilderPage />),
  capability('/customer-lifecycle/day1', 'Day1 Plan', <Day1PlanPage />),
  capability('/customer-lifecycle/day1/:planId', 'Day1 Plan', <Day1PlanPage />),
  capability('/customer-lifecycle/presentations', 'Presentation Maker', <PresentationMaker />),
  capability('/procurement', 'Procurement Overview', <ProcurementOverview />),
  capability('/procurement/products', 'Product Search', <ProductSearch />),
  capability('/procurement/orders', 'Purchase Orders', <PurchaseOrdersPage />),
  capability('/procurement/orders/:id', 'Purchase Order Detail', <PurchaseOrderDetailPage />),
  capability('/procurement/tracking', 'Order Tracking', <OrderTrackingPage />),
  capability('/procurement/hardware-profiles', 'Hardware Profiles', <HardwareProfiles />),
  capability('/procurement/bundles', 'Procurement Bundles', <ProcurementBundles />),
  capability('/procurement/microsoft-licenses', 'Microsoft Licenses', <MicrosoftLicenses />),
  capability('/procurement/procurement', 'Procurement', <ProcurementPage />),
  capability('/procurement/procurement/:id', 'Procurement', <ProcurementPage />),
  ...SERVICE_OPERATIONS_ROUTE_DEFINITIONS.map(({ capabilityPath, label, element }) =>
    capability(capabilityPath, label, element)
  ),
  capability('/billing', 'Billing Dashboard', <BillingDashboard />),
  capability('/calls/voicemails', 'Voicemail Dashboard', <VoicemailDashboard />),
  capability('/identity-lifecycle', 'Identity Lifecycle Dashboard', <IdentityLifecycleDashboard />),
  capability('/identity-lifecycle/onboarding', 'Onboarding Wizard', <OnboardingWizard />),
  capability('/identity-lifecycle/offboarding', 'Offboarding Wizard', <OffboardingWizard />),
  capability(
    '/identity-lifecycle/:type/workflow/:workflowId',
    'Workflow Progress',
    <WorkflowProgress />
  ),
  capability('/audit/:id', 'Audit Editor', <AuditEditor />),
  capability('/roadmaps', 'Roadmap List', <RoadmapList />),
  capability('/roadmaps/:id', 'Roadmap Editor', <RoadmapEditor />),
  capability('/tech-roadmaps', 'Tech Roadmap List', <TechRoadmapList />),
  capability('/tech-roadmaps/:roadmapId', 'Tech Roadmap Editor', <TechRoadmapEditor />),
  capability(
    '/customer-lifecycle/tech-roadmap-templates',
    'Tech Roadmap Templates',
    <TechRoadmapTemplates />
  ),
  capability('/roadmaps/:id/compose', 'Roadmap Builder', <RoadmapBuilderPage />),
  capability('/narratives', 'Narrative List', <NarrativeList />),
  capability('/narratives/new', 'Narrative Kind Picker', <NarrativeKindPicker />),
  capability('/narratives/new/:kind', 'Narrative Start Paths', <NarrativeStartPaths />),
  capability('/narratives/:id/compose', 'Narrative Builder', <NarrativeBuilderPage />),
  capability('/qbr/:id/compose', 'Narrative Builder', <NarrativeBuilderPage defaultKind="qbr" />),
  capability('/customer-lifecycle/estimator', 'MSA Builder', <MSABuilder />),
  capability('/customer-lifecycle/estimator/list', 'Estimate List', <EstimateList />),
  capability(
    '/customer-lifecycle/estimator/catalogue',
    'Benchmark Catalogue',
    <BenchmarkCatalogue />
  ),
  capability('/customer-lifecycle/estimator/compare', 'MSA Comparator', <MSAComparator />),
  capability('/customer-lifecycle/estimator/terms', 'Service Terms', <ServiceTerms />),
  capability('/customer-lifecycle/estimator/rules', 'Assessor Rules', <AssessorRules />),
  capability('/customer-lifecycle/estimator/:id', 'MSA Builder', <MSABuilder />),
  capability('/cloud-governance', 'Cloud Governance', <CloudGovernanceDashboard />),
  capability('/cloud-governance/rate-limits', 'Rate Limits', <RateLimitsPage />),
  capability('/cloud-governance/recordsroom', 'Recordsroom', <RecordsroomOperationsPage />),
  capability(
    '/communications-migration',
    'Communications Migration Dashboard',
    <CommunicationsMigrationDashboard />
  ),
  capability('/communications-migration/export', 'Data Export', <DataExportPage />),
  capability('/communications-migration/history', 'Migration History', <MigrationHistoryPage />),
  capability(
    '/communications-migration/assessment',
    'Migration Assessment',
    <MigrationAssessment />
  ),
  ...COMMUNICATIONS_ROUTE_DEFINITIONS.map(({ capabilityPath, label, element }) =>
    capability(capabilityPath, label, element)
  ),
  ...MICROSOFT_365_MANAGEMENT_ROUTE_DEFINITIONS.map(({ capabilityPath, label, element }) =>
    capability(capabilityPath, label, element)
  ),
  ...WORKFLOW_AUTOMATION_ROUTE_DEFINITIONS.map(({ capabilityPath, label, element }) =>
    capability(capabilityPath, label, element)
  ),
  ...PROJECT_DELIVERY_ROUTE_DEFINITIONS.map(({ capabilityPath, label, element }) =>
    capability(capabilityPath, label, element)
  ),
  ...REMOTE_ACCESS_ROUTE_DEFINITIONS.map(({ capabilityPath, label, element }) =>
    capability(capabilityPath, label, element)
  ),
  capability('/proposals/:proposalId/compose', 'Proposal Composer', <ProposalComposer />),
  capability('/proposals/:proposalId/wizard', 'Proposal Wizard', <ProposalWizardPage />),
  capability('/proposals/new', 'Proposal Wizard', <ProposalWizardPage />),
  capability('/vault', 'Credential Vault', <CredentialVault />),
  capability('/vault/credentials/:credentialId', 'Credential Vault', <CredentialVault />),
  capability(
    '/vault/health/scans/:id',
    'Credential Health Scan Detail',
    <CredentialHealthScanDetail />
  ),
  capability('/assets', 'Asset Dashboard', <AssetDashboard />),
  capability('/assets/certificates', 'Certificate List', <CertificateList />),
  capability('/assets/licenses', 'License Inventory', <LicenseInventory />),
  capability('/assets/renewals', 'Renewal Calendar', <RenewalCalendar />),
  capability('/assets/warranties', 'Warranty Tracker', <WarrantyTracker />),
  capability('/assets/leases', 'Lease Tracker', <LeaseTracker />),
  capability('/assets/disposal', 'Asset Disposal', <AssetDisposal />),
  capability('/assets/refresh-cycle', 'Refresh Cycle', <RefreshCycle />),
  capability('/assets/registry', 'Asset Registry', <AssetRegistry />),
  capability('/assets/service-desk', 'Service Desk', <ServiceDesk />),
  capability('/assets/site-docs', 'Site Documentation', <SiteDocumentation />),
  capability('/assets/uptime', 'Uptime Monitor', <UptimeMonitor />),
  capability('/assets/domains', 'Domain Tracker', <DomainTracker />),
  capability('/backups', 'Backup Dashboard', <BackupDashboard />),
  capability('/backups/providers', 'Backup Providers', <BackupProviders />),
  capability('/backups/providers/new', 'Backup Provider Wizard', <BackupProviderWizard />),
  capability('/backups/jobs', 'Backup Jobs', <BackupJobs />),
  capability('/backups/compliance', 'Backup Compliance', <BackupCompliance />),
  capability('/backups/reports', 'Backup Reports', <BackupReports />),
  capability('/backups/alerts', 'Backup Alerts', <BackupAlerts />),
  capability('/backups/scopes', 'Backup Asset Scopes', <BackupAssetScopes />),
  capability('/backups/restore-tests', 'Restore Test History', <RestoreTestHistory />),
  capability('/backups/restore-tests/new', 'Restore Test Wizard', <RestoreTestWizard />),
  capability('/drift/baselines', 'Baseline Management', <BaselineManagement />),
  capability('/configuration-drift/policies', 'Monitoring Policies', <MonitoringPolicies />),
  capability('/drift/policies', 'Monitoring Policies', <MonitoringPolicies />),
  capability(
    '/automation/scripts',
    'Automation Scripts',
    <ScriptLegacyRedirect segment="library" />
  ),
  capability(
    '/automation/workflows',
    'Automation Workflows',
    <ScriptLegacyRedirect segment="workflows" />
  ),
  capability(
    '/automation/executions',
    'Script Executions',
    <ScriptLegacyRedirect segment="executions" />
  ),
  capability('/automation/rules', 'Remediation Rules', <ScriptLegacyRedirect segment="rules" />),
  capability(
    '/automation/schedules',
    'Scheduled Tasks',
    <ScriptLegacyRedirect segment="schedules" />
  ),
  capability('/automation/webhooks', 'Webhook Config', <ScriptLegacyRedirect segment="webhooks" />),
  capability('/m365/usage', 'Usage Analytics', <UsageAnalytics />),
  capability('/m365/inactive-users', 'Inactive Users', <InactiveUsers />),
  capability('/m365/sign-in-logs', 'Sign In Logs', <SignInLogs />),
  capability('/m365/risky-agents', 'Risky Agents', <RiskyAgents />),
  capability('/m365/intune-remediations', 'Intune Remediations', <IntuneRemediationsPage />),
  capability(
    '/m365/intune-enrollment-posture',
    'Intune Enrollment Posture',
    <IntuneEnrollmentPosturePage />
  ),
  capability('/m365/intune-app-approvals', 'Intune App Approvals', <IntuneAppApprovalsPage />),
  capability('/m365/power-automate', 'Power Automate Inventory', <PowerAutomateInventoryPage />),
  capability('/assessor/deadlines', 'Deadline Readiness', <DeadlineReadiness />),
  capability('/security/vulnerabilities', 'Vulnerability Dashboard', <VulnerabilityDashboard />),
  capability('/security/incidents', 'Security Incidents', <SecurityIncidents />),
  capability('/security/incidents/report', 'Quarterly Report', <QuarterlyReportPage />),
  capability('/security/incidents/:id', 'Incident Cockpit', <IncidentCockpit />),
  capability('/compliance/*', 'Compliance Routes', <ComplianceRoutes />),
  capability('/tenant-setup', 'MSP Setup Hub', <MSPSetupHub />),
  capability('/tenant-setup/import', 'PSA Import', <PsaImportPage />),
  capability(
    '/device-management/endpoint-runtime',
    'Endpoint Runtime Dashboard',
    <EndpointRuntimeDashboard />
  ),
  capability('/device-management/auto-provision', 'Auto Provision', <AutoProvisionPage />),
  capability(
    '/tenant-administration',
    'Tenant Administration Dashboard',
    <TenantAdministrationAccessGuard>
      <TenantAdministrationDashboard />
    </TenantAdministrationAccessGuard>
  ),
  capability(
    '/tenant-administration/tenants',
    'Tenant List',
    <TenantAdministrationAccessGuard>
      <TenantList />
    </TenantAdministrationAccessGuard>
  ),
  capability('/billing/subscribe', 'Billing', <BillingPage />),
  capability('/billing/success', 'Billing Success', <BillingSuccess />),
  capability('/billing/cancel', 'Billing Cancel', <BillingCancel />),
  capability('/migration/device', 'Device Migration Dashboard', <DeviceMigrationDashboard />),
  capability('/migration/device/new', 'Device Migration Wizard', <DeviceMigrationWizard />),
  capability(
    '/migration/gworkspace/new',
    'G Workspace Migration Wizard',
    <GWorkspaceMigrationWizard />
  ),
  capability('/migration/device/:id', 'Device Migration Detail', <DeviceMigrationDetail />),
  capability('/migration/profile/:id', 'Profile Migration', <ProfileMigrationPage />),
  capability('/reports/history', 'Report History', <ReportHistory />),
  capability('/reports/schedules', 'Report Schedules', <ReportSchedules />),
  capability('/search', 'Global Search', <GlobalSearch />),
  capability('/notifications', 'Notification Center', <NotificationCenter />),
  capability(
    '/admin/tenant-compare',
    'Tenant Comparison',
    <TenantAdministrationAccessGuard>
      <TenantComparisonPage />
    </TenantAdministrationAccessGuard>
  ),
  capability('/admin/scan-schedules', 'Scan Schedule Templates', <ScanScheduleTemplates />),
  capability('/admin/kb/new', 'KB Editor', <KBEditor />),
  capability('/admin/kb/:id', 'KB Editor', <KBEditor />),
  capability('/insights/benchmark', 'Client Benchmark', <ClientBenchmark />),
  capability('/insights/compliance-matrix', 'Compliance Matrix', <ComplianceMatrix />),
  capability('/insights/resolution-feed', 'Resolution Feed Insight', <ResolutionFeedInsight />),
  capability('/insights/delivery-pipeline', 'Delivery Pipeline', <DeliveryPipeline />),
  capability('/insights/engineer-workload', 'Engineer Workload', <EngineerWorkload />),
  capability('/insights/client-health', 'Client Health Board', <ClientHealthBoard />),
  capability('/insights/client/:customerId', 'Client Deep Dive', <ClientDeepDive />),
  capability('/insights/cost-of-inaction', 'Cost Of Inaction View', <CostOfInactionView />),
  capability('/insights/automation', 'Automation Opportunity', <AutomationOpportunity />),
  capability('/insights/revenue', 'Revenue Profitability', <RevenueProfitability />),
  capability(
    '/customer-lifecycle/satisfaction',
    'Satisfaction Dashboard',
    <SatisfactionDashboard />
  ),
  capability('/customer-lifecycle/risk-register', 'Risk Register', <RiskRegister />),
  capability('/device-management/fleet-health', 'Fleet Health Dashboard', <FleetHealthDashboard />),
  capability('/admin/devices/policies', 'Policy Management', <PolicyManagement />),
  capability('/admin/devices/policies/:id', 'Policy Management', <PolicyManagement />),
  capability('/admin/devices/agent-config', 'Agent Config Policies', <AgentConfigPolicies />),
  capability('/admin/devices/alert-catalog', 'Alert Catalog', <AlertCatalog />),
  capability('/admin/devices/alert-dedup', 'Alert Dedup Rules', <AlertDedupRules />),
  capability('/admin/devices/group-rules', 'Device Group Rules', <DeviceGroupRules />),
  capability('/admin/operations', 'Operations Dashboard', <OperationsDashboardPage />),
  capability('/changes/pending', 'Pending Changes', <PendingChangesPage />),
  capability('/admin/insights', 'Insights', <InsightsPage />),
  capability('/personas', 'Persona List', <PersonaList />),
  capability('/personas/new', 'Persona Editor', <PersonaEditor />),
  capability('/personas/:id/edit', 'Persona Editor', <PersonaEditor />),
  capability('/personas/queue', 'Persona Action Queue', <PersonaActionQueue />),
  capability('/personas/logs', 'Persona Activity Log', <PersonaActivityLog />),
  capability('/trust-stream', 'Trust Stream', <TrustStreamPage />),
  capability('/disruption/plans', 'Planning Queue', <PlanningQueue />),
] as const;

export function capabilitiesForDestination(
  destination: DestinationId
): readonly DestinationCapabilityDefinition[] {
  return DESTINATION_CAPABILITIES.filter((capability) => capability.destination === destination);
}

/** Route elements are generated from capability records so functions do not
 * become hand-authored product surfaces again. */
export function destinationCapabilityRouteElements(destination: DestinationId): ReactElement[] {
  return capabilitiesForDestination(destination).flatMap((capability) => [
    // A concrete client route (:id/reports) outranks compliance/*. Keep the
    // report page inside its capability frame while reserving its exact path.
    ...(capability.capabilityPath === '/compliance/*'
      ? [
          <Route
            key="compliance/reports"
            path="compliance/reports"
            element={
              <DestinationCapabilityFrame
                capability={{
                  ...capability,
                  element: complianceRoutes.find((route) => route.path === 'reports')!
                    .element as ReactElement,
                }}
              />
            }
          />,
        ]
      : []),
    <Route
      key={capability.capabilityPath}
      path={capability.routePath}
      element={<DestinationCapabilityFrame capability={capability} />}
    />,
  ]);
}
